import { DateTime } from 'luxon'
import {
  getName,
  isDefined,
  isEmpty,
  isNotEmpty,
  type ReportColumnValue,
  type ReportKpiFilter,
  type ReportKpiGrouping,
  type ReportKpiType,
  type QuestionData,
  type QuestionRecurrence,
  type TeamData,
  type TeamId,
  type TinyTeam,
  type TinyUser,
  range,
} from '@terros/common'
import { type QueryKey } from '@tanstack/react-query'
import { type DecoratedReportKpiRow, toValidNumberValue } from '../report'
import { type QuestionManagerBreakdownRow, type QuestionResponseCounts, type QuestionTrendBucket } from './reportTypes'

export const GROUP_BY_KPI = 'groupBy' as ReportKpiType

export type QuestionReportKpis = {
  groupBy: ReportKpiType
  choiceIndexes: number[]
  choices: ReportKpiType[]
  yes: ReportKpiType
  no: ReportKpiType
  skip: ReportKpiType
  answer: ReportKpiType
  all: ReportKpiType[]
}

export type QuestionReportRange = {
  startDate: number
  endDate: number
}

export const getQuestionReportKpis = (
  question: Pick<QuestionData, 'questionId'> & Partial<Pick<QuestionData, 'choiceType' | 'choices'>>
): QuestionReportKpis => {
  const yes = `question|${question.questionId}|choice|1` as ReportKpiType
  const no = `question|${question.questionId}|choice|0` as ReportKpiType
  const skip = `question|${question.questionId}|skip` as ReportKpiType
  const answer = `question|${question.questionId}|answer` as ReportKpiType
  const choiceIndexes = getQuestionReportChoiceIndexes(question)
  const choices = choiceIndexes.map(
    (choiceIndex) => `question|${question.questionId}|choice|${choiceIndex}` as ReportKpiType
  )
  return {
    groupBy: GROUP_BY_KPI,
    choiceIndexes,
    choices,
    yes,
    no,
    skip,
    answer,
    all: [GROUP_BY_KPI, ...choices, skip, answer],
  }
}

export const getQuestionReportChoiceIndexes = (
  question: Partial<Pick<QuestionData, 'choiceType' | 'choices'>>
): number[] => {
  if (question.choiceType === 'RatingScale') return range(1, 5)
  const choiceCount = question.choiceType === 'MultipleChoice' ? (question.choices?.length ?? 0) : 2
  return Array.from({ length: choiceCount }, (_, choiceIndex) => choiceIndex)
}

export const getQuestionReportKpiQueryKey = (
  question: Pick<QuestionData, 'questionId'> & Partial<Pick<QuestionData, 'choiceType' | 'choices'>>
): QueryKey => ['report-kpi', { kpis: getQuestionReportKpis(question).all.map((value) => ({ value })), version: 2 }]

export const getReportPeriodFromQuestion = (question: QuestionData): QuestionRecurrence => {
  switch (question.recurs) {
    case 'week':
      return 'week'
    case 'quarter':
      return 'quarter'
    case 'year':
      return 'year'
    case 'month':
    case undefined:
      return 'month'
  }
}

export const getLast12MonthsRange = (timezone: string, now = Date.now()): QuestionReportRange => {
  const end = DateTime.fromMillis(now, { zone: timezone }).endOf('month')
  return { startDate: end.startOf('month').minus({ months: 11 }).toMillis(), endDate: end.toMillis() }
}

export const getCalendarYearRange = (year: number, timezone: string): QuestionReportRange => {
  const start = DateTime.fromObject({ year }, { zone: timezone }).startOf('year')
  return {
    startDate: start.toMillis(),
    endDate: start.endOf('year').toMillis(),
  }
}

export type QuestionReportYearBounds = Pick<QuestionData, 'startDate' | 'endDate' | 'createdAt'>

export const getCurrentCalendarYear = (timezone: string, now = Date.now()): number => {
  return DateTime.fromMillis(now, { zone: timezone }).year
}

export const getQuestionReportYears = (
  question: QuestionReportYearBounds,
  timezone: string,
  now = Date.now()
): number[] => {
  const currentYear = getCurrentCalendarYear(timezone, now)
  const startMs = question.startDate ?? question.createdAt ?? now
  const endMs = question.endDate ?? now
  const startYear = DateTime.fromMillis(startMs, { zone: timezone }).year
  const endYear = Math.min(DateTime.fromMillis(endMs, { zone: timezone }).year, currentYear)
  if (startYear > endYear) return [endYear]

  const years: number[] = []
  for (let year = endYear; year >= startYear; year--) years.push(year)
  return years
}

export const getDefaultQuestionReportYear = (years: number[], timezone: string, now = Date.now()): number => {
  const currentYear = getCurrentCalendarYear(timezone, now)
  if (years.includes(currentYear)) return currentYear
  return years[0] ?? currentYear
}

export const resolveQuestionReportYear = (
  year: number | null | undefined,
  years: number[],
  timezone: string,
  now = Date.now()
): number => {
  if (isDefined(year) && years.includes(year)) return year
  return getDefaultQuestionReportYear(years, timezone, now)
}

export const withReportRange = (baseFilter: ReportKpiFilter, range: QuestionReportRange): ReportKpiFilter => ({
  ...baseFilter,
  ...range,
})

export const getLast12MonthsFilter = withReportRange

export const toQuestionCounts = (
  rows: DecoratedReportKpiRow[] | undefined,
  kpis: QuestionReportKpis
): QuestionResponseCounts => {
  const choiceCounts = kpis.choices.map(
    (choice) => rows?.reduce((total, row) => total + toValidNumberValue(row[choice]), 0) ?? 0
  )
  const counts: QuestionResponseCounts = {
    choiceCounts,
    yesCount: choiceCounts[1] ?? 0,
    noCount: choiceCounts[0] ?? 0,
    skipCount: 0,
    answerCount: 0,
  }
  rows?.forEach((row) => {
    counts.skipCount += toValidNumberValue(row[kpis.skip])
    counts.answerCount += toValidNumberValue(row[kpis.answer])
  })
  return counts
}

export const toTrendBuckets = (
  rows: DecoratedReportKpiRow[] | undefined,
  kpis: QuestionReportKpis,
  period: QuestionRecurrence,
  range: QuestionReportRange,
  timezone: string
): QuestionTrendBucket[] => {
  const rowByStartDate = new Map<number, DecoratedReportKpiRow>()
  rows?.forEach((row) => {
    const startDate = toValidNumberValue(row[GROUP_BY_KPI])
    if (startDate > 0)
      rowByStartDate.set(DateTime.fromMillis(startDate, { zone: timezone }).startOf(period).toMillis(), row)
  })

  const buckets: QuestionTrendBucket[] = []
  const rangeStart = DateTime.fromMillis(range.startDate, { zone: timezone })
  const rangeEnd = DateTime.fromMillis(range.endDate, { zone: timezone })
  let cursor = rangeStart.startOf(period)
  while (cursor <= rangeEnd) {
    const periodStart = cursor.toMillis()
    const nextPeriod = plusPeriod(cursor, period)
    const row = rowByStartDate.get(periodStart)
    buckets.push({
      label: formatBucketLabel(cursor, period),
      startDate: Math.max(periodStart, range.startDate),
      endDate: Math.min(nextPeriod.minus({ milliseconds: 1 }).toMillis(), range.endDate),
      choiceCounts: kpis.choices.map((choice) => toValidNumberValue(row?.[choice])),
      yesCount: toValidNumberValue(row?.[kpis.yes]),
      noCount: toValidNumberValue(row?.[kpis.no]),
      skipCount: toValidNumberValue(row?.[kpis.skip]),
      answerCount: toValidNumberValue(row?.[kpis.answer]),
    })
    cursor = nextPeriod
  }
  return buckets
}

export const toTeamCounts = (
  rows: DecoratedReportKpiRow[] | undefined,
  kpis: QuestionReportKpis
): Map<TeamId, QuestionResponseCounts> => {
  const countsByTeamId = new Map<TeamId, QuestionResponseCounts>()
  rows?.forEach((row) => {
    const groupBy = row[GROUP_BY_KPI]
    if (!isTinyTeam(groupBy)) return
    const counts = toCountsFromRow(row, kpis)
    if (isEmpty(counts.answerCount) && isEmpty(counts.skipCount)) return
    countsByTeamId.set(groupBy.teamId, counts)
  })
  return countsByTeamId
}

export const toManagerRows = (
  rows: DecoratedReportKpiRow[] | undefined,
  kpis: QuestionReportKpis
): QuestionManagerBreakdownRow[] => {
  return (
    rows
      ?.map((row) => toManagerRow(row, kpis))
      .filter(isDefined)
      .filter((row) => isNotEmpty(row.answerCount)) ?? []
  )
}

export const toTeamGrouping = (level: number | undefined, teams: TeamData[]): ReportKpiGrouping => ({
  type: 'team',
  level: level ?? 1,
  include: teams.map((team) => team.teamId),
})

const toManagerRow = (
  row: DecoratedReportKpiRow,
  kpis: QuestionReportKpis
): QuestionManagerBreakdownRow | undefined => {
  const groupBy = row[GROUP_BY_KPI]
  if (!isTinyUser(groupBy)) return
  const name = getName(groupBy)
  return {
    ...toCountsFromRow(row, kpis),
    id: groupBy.userId,
    name,
    kind: 'manager',
  }
}

const toCountsFromRow = (row: DecoratedReportKpiRow | undefined, kpis: QuestionReportKpis): QuestionResponseCounts => ({
  choiceCounts: kpis.choices.map((choice) => toValidNumberValue(row?.[choice])),
  yesCount: toValidNumberValue(row?.[kpis.yes]),
  noCount: toValidNumberValue(row?.[kpis.no]),
  skipCount: toValidNumberValue(row?.[kpis.skip]),
  answerCount: toValidNumberValue(row?.[kpis.answer]),
})

export const getQuestionAverageScore = (choiceCounts: number[], choiceIndexes: number[]): number | undefined => {
  if (choiceIndexes.length !== 5 || choiceIndexes[0] !== 1 || choiceIndexes.at(-1) !== 5) return
  const answerCount = choiceCounts.reduce((total, count) => total + count, 0)
  if (isEmpty(answerCount)) return
  const scoreTotal = choiceCounts.reduce((total, count, index) => total + count * (choiceIndexes[index] ?? 0), 0)
  return scoreTotal / answerCount
}

const isTinyTeam = (value: ReportColumnValue | undefined): value is TinyTeam => {
  return typeof value === 'object' && value !== null && 'teamId' in value
}

const isTinyUser = (value: ReportColumnValue | undefined): value is TinyUser => {
  return typeof value === 'object' && value !== null && 'userId' in value
}

const plusPeriod = (date: DateTime, period: QuestionRecurrence): DateTime => {
  switch (period) {
    case 'week':
      return date.plus({ weeks: 1 })
    case 'quarter':
      return date.plus({ quarters: 1 })
    case 'year':
      return date.plus({ years: 1 })
    case 'month':
      return date.plus({ months: 1 })
  }
}

const formatBucketLabel = (date: DateTime, period: QuestionRecurrence): string => {
  switch (period) {
    case 'week':
      return date.toFormat('MMM d')
    case 'quarter':
      return `Q${date.quarter}`
    case 'year':
      return date.toFormat('yyyy')
    case 'month':
      return date.toFormat('MMM')
  }
}
